import type { ReactElement } from 'react';
import { classNames } from '@/lib/utils';
import type { MapPopupBadgeVariant, MapPopupField, MapPopupProps } from './MapPopup.types';

const badgeStyles: Record<MapPopupBadgeVariant, string> = {
  default: 'bg-overlay-light text-white',
  info: 'bg-sky-500/20 text-sky-200 ring-1 ring-inset ring-sky-500/40',
  success: 'bg-emerald-500/20 text-emerald-200 ring-1 ring-inset ring-emerald-500/40',
  warning: 'bg-amber-500/20 text-amber-100 ring-1 ring-inset ring-amber-500/40',
  danger: 'bg-rose-500/20 text-rose-100 ring-1 ring-inset ring-rose-500/40',
  critical: 'bg-status-red-bg30 text-red-50 ring-1 ring-inset ring-red-500/50',
};

const fieldStatusClasses = {
  default: 'text-white',
  muted: 'text-overlay-text65',
  warning: 'text-amber-300',
  danger: 'text-red-300',
  success: 'text-emerald-300',
} as const;

function renderFieldContent(field: MapPopupField, index: number): ReactElement {
  if (field.content) {
    return (
      <div
        key={field.id ?? `field-${index}`}
        className="rounded-xl border border-overlay-border10 bg-overlay-lightest px-3 py-2 text-sm text-white"
      >
        {field.content}
      </div>
    );
  }

  const statusClass = fieldStatusClasses[field.status ?? 'default'];

  return (
    <div key={field.id ?? `${field.label ?? 'field'}-${index}`} className="flex flex-col gap-1">
      {field.label && (
        <p className="text-[11px] font-semibold uppercase tracking-wide text-overlay-text50">{field.label}</p>
      )}
      <div className="flex items-center gap-2">
        {field.iconSrc ? (
          <img
            src={field.iconSrc}
            alt={field.iconAlt ?? ''}
            className="h-5 w-7 rounded-sm border border-overlay-border15 object-cover"
            loading="lazy"
          />
        ) : null}
        <p className={classNames('text-sm font-medium leading-5', statusClass, field.valueClassName)}>
          {field.value ?? '—'}
        </p>
      </div>
      {field.hint ? <p className="text-xs text-overlay-text60">{field.hint}</p> : null}
    </div>
  );
}

export function MapPopup({
  badge,
  title,
  subtitle,
  description,
  meta,
  icon,
  media,
  sections,
  footer,
  className,
}: MapPopupProps) {
  const hasHeaderContent = badge || title || subtitle || description || (meta && meta.length > 0) || icon;

  return (
    <div
      className={classNames(
        'pointer-events-auto min-w-[240px] max-w-[360px] overflow-hidden rounded-2xl border border-overlay-border10 bg-slate-950/95 text-white shadow-2xl ring-1 ring-overlay-border10 backdrop-blur-xl',
        className,
      )}
    >
      {media ? (
        <div
          className="relative w-full overflow-hidden aspect-video"
          style={media.aspectRatio ? { aspectRatio: media.aspectRatio } : undefined}
        >
          {media.type === 'image' ? (
            <img
              id={media.id}
              src={media.src}
              alt={media.alt ?? ''}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <video
              id={media.id}
              src={media.src}
              poster={media.poster}
              loop={media.loop ?? true}
              autoPlay={media.autoPlay ?? true}
              muted={media.muted ?? true}
              controls={media.controls ?? false}
              playsInline
              className="h-full w-full object-cover"
            />
          )}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-950/90 to-transparent" />
        </div>
      ) : null}

      <div className="flex flex-col gap-4 p-4">
        {hasHeaderContent ? (
          <div className="flex flex-col gap-3">
            {badge ? (
              <span
                className={classNames(
                  'inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                  badgeStyles[badge.variant ?? 'default'],
                )}
              >
                {badge.label}
              </span>
            ) : null}
            <div className="flex items-start gap-3">
              {icon ? (
                <img
                  src={icon.src}
                  alt={icon.alt ?? ''}
                  className={classNames(
                    'h-8 w-11 border border-overlay-border15 object-cover',
                    icon.shape === 'circle' ? 'rounded-full' : icon.shape === 'square' ? 'rounded-md' : 'rounded-lg',
                  )}
                  loading="lazy"
                />
              ) : null}
              <div className="min-w-0 flex-1">
                {title ? <h3 className="text-lg font-semibold leading-6 text-white">{title}</h3> : null}
                {subtitle ? <p className="text-sm text-overlay-text70">{subtitle}</p> : null}
              </div>
            </div>
            {description ? <p className="text-sm text-overlay-text75">{description}</p> : null}
            {meta && meta.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {meta.map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-overlay-border15 px-2.5 py-0.5 text-[11px] uppercase tracking-wide text-overlay-text70"
                  >
                    {item}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {sections && sections.length > 0 ? (
          <div className="flex flex-col gap-4">
            {sections.map((section) => (
              <div key={section.id} className="flex flex-col gap-3">
                {section.title ? (
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-200">{section.title}</p>
                ) : null}
                {section.description ? <p className="text-sm text-overlay-text70">{section.description}</p> : null}
                <div
                  className={classNames(
                    'grid gap-3',
                    section.columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
                    section.dense ? 'gap-2' : 'gap-3',
                  )}
                >
                  {section.fields.map((field, index) => renderFieldContent(field, index))}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {footer ? (
          <div className="border-t border-overlay-border10 pt-3 text-sm text-overlay-text70">
            <div className="flex items-center justify-between">{footer}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
